// repoApi.js — analyze → check → save/deploy repository contract.
// Stateless: analyze lists services, check reads repo/branch state, save/deploy
// resend the SAME `source`. All calls POST the process backend (backendUrl, :8083)
// with the `x-access-token` header. JSON bodies, or multipart when a source carries
// an uploaded archive (caller builds the FormData). See FRONTEND_REPOSITORY_SAVE.md.

// Gather the generated project's service paths for `source: { type:"generated", projectPaths }`.
export function collectProjectPaths(agentUsed, agentResult) {
  const r = agentResult || {};
  if (agentUsed === "springboot") return [r.projectPath].filter(Boolean);
  if (agentUsed === "dotnet") {
    const list = (r.projectPaths || (r.projects || []).map((p) => p.path || p.projectPath) || []).filter(Boolean);
    return list.length ? list : [r.projectPath].filter(Boolean);
  }
  if (agentUsed === "mule") {
    return (r.apis && r.apis.length)
      ? r.apis.filter((a) => a.projectPath).map((a) => a.projectPath)
      : [r.projectPath].filter(Boolean);
  }
  return [r.projectPath].filter(Boolean);
}

async function post(url, token, body, timeoutMs, isForm) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const headers = isForm ? { "x-access-token": token } : { "Content-Type": "application/json", "x-access-token": token };
    const r = await fetch(url, { method: "POST", headers, body: isForm ? body : JSON.stringify(body), signal: ctrl.signal });
    const data = await r.json().catch(() => ({}));
    return { ok: r.ok, status: r.status, data };
  } catch (e) {
    return { ok: false, status: 0, data: { success: false, code: "NETWORK", error: e.name === "AbortError" ? "Request timed out." : "Could not reach the server." } };
  } finally {
    clearTimeout(t);
  }
}

export function analyzeProject(backendUrl, token, body) {
  return post(`${backendUrl}/api/v1/project/analyze`, token, body, 120000, body instanceof FormData);
}
export function checkRepositories(backendUrl, token, body) {
  return post(`${backendUrl}/api/v1/repository/check`, token, body, 60000, false);
}
export function saveRepository(backendUrl, token, body) {
  return post(`${backendUrl}/api/v1/repository/save`, token, body, 600000, body instanceof FormData);
}

// Machine-readable error code → user message (FRONTEND_REPOSITORY_SAVE.md §6).
export const SAVE_ERROR_MESSAGES = {
  VALIDATION_FAILED: "Please check the highlighted field.",
  BRANCH_REQUIRED: "A branch is required for repositories that already have content. Nothing was written.",
  BASE_BRANCH_NOT_FOUND: "That base branch doesn't exist in the repository. Nothing was written.",
  MISSING_TARGETS: "Pick a repository for every service.",
  PROJECT_NOT_FOUND: "The generated project is no longer available — generate it again.",
  SAVE_IN_PROGRESS: "A save is already running for this session.",
  BRANCH_CONFLICT: "The branch moved while saving — try again.",
  BRANCH_BASE_MISSING: "The repository has no default branch to branch from.",
  ARCHIVE_TOO_LARGE: "The upload is too large.",
  INVALID_LAYOUT: "That layout isn't allowed for this project.",
  UNSUPPORTED_PROJECT: "This project can't be saved.",
  AUTH_FAILED: "The token/PAT was rejected — re-link the account in Connected Apps.",
  PERMISSION_DENIED: "Permission denied for that account.",
  PROVIDER_ERROR: "The git provider returned an error.",
  PROJECT_CREATE_FAILED: "Azure DevOps could not create the project.",
  RATE_LIMITED: "Too many attempts — try again shortly.",
  NETWORK: "Could not reach the server.",
};
